/**
 * Results Display Manager
 *
 * Handles the display and interaction of search results for the Object Finder.
 * Provides filtering, sorting, and detailed object information display.
 */

import { chooseDisplayName, cleanObjectName, objectTypeLabel } from './displayNames.js';

export class ResultsDisplay {
    constructor() {
        this.currentResults = [];
        this.currentSearchParams = null;
        this.displayedResults = [];
        this.currentTier = 'all';
        this.currentSort = 'score';
        this.maxResults = 25;
        this.isInitialized = false;
        this.aliasIndexPromise = null;
    }

    /**
     * The IndexedDB manager of the current page, if any
     * @returns {Object|null}
     */
    getDbManager() {
        if (typeof window === 'undefined') return null;
        if (window.app && window.app.databaseManager) return window.app.databaseManager;
        if (window.objectFinder && window.objectFinder.searchEngine && window.objectFinder.searchEngine.dbManager) {
            return window.objectFinder.searchEngine.dbManager;
        }
        return null;
    }

    /**
     * Alias index from the static JSON (fallback when there is no database):
     * downloaded and indexed once, shared by every caller
     * @returns {Promise<Map<string, string[]>>} object name -> aliases
     */
    getAliasIndex() {
        if (!this.aliasIndexPromise) {
            this.aliasIndexPromise = (async () => {
                const index = new Map();
                try {
                    const response = await fetch('./data/object_aliases.json');
                    if (response.ok) {
                        for (const { alias, object_name: objectName } of await response.json()) {
                            if (!index.has(objectName)) index.set(objectName, []);
                            index.get(objectName).push(alias);
                        }
                    }
                } catch (error) {
                    console.log('Could not load aliases:', error);
                }
                return index;
            })();
        }
        return this.aliasIndexPromise;
    }

    /**
     * Display name and aliases for an object (database first, JSON fallback)
     * @param {Object} obj - Result object
     * @returns {Promise<{displayName: string, aliases: string[]}>}
     */
    async resolveNames(obj) {
        let aliases = [];
        const dbManager = this.getDbManager();
        if (dbManager) {
            try {
                if (obj.id !== undefined) {
                    aliases = (await dbManager.getObjectAliases(obj.id)).map((alias) => alias.alias_name);
                }
            } catch (error) {
                console.log('Database alias lookup failed for', obj.name, error);
            }
        } else {
            aliases = (await this.getAliasIndex()).get(obj.name) || [];
        }
        return { displayName: chooseDisplayName(obj, aliases), aliases };
    }

    /**
     * Initialize the results display
     */
    initialize() {
        if (this.isInitialized) {
            return;
        }

        try {
            this.setupEventListeners();
            this.isInitialized = true;
            console.log('Results Display initialized');
        } catch (error) {
            console.error('Failed to initialize Results Display:', error);
        }
    }

    /**
     * Set up event listeners for results interactions
     */
    setupEventListeners() {
        // Quality tier tabs
        const qualityTabs = document.querySelectorAll('#qualityTabs .nav-link');
        qualityTabs.forEach(tab => {
            tab.addEventListener('click', async (e) => {
                e.preventDefault();
                const tier = e.target.closest('a').getAttribute('data-tier');
                await this.setQualityTier(tier);
            });
        });

        // Sort dropdown
        const sortBy = document.getElementById('sortBy');
        if (sortBy) {
            sortBy.addEventListener('change', async (e) => {
                await this.setSortCriteria(e.target.value);
            });
        }

        // Max results dropdown
        const maxResults = document.getElementById('maxResults');
        if (maxResults) {
            maxResults.addEventListener('change', async (e) => {
                await this.setMaxResults(parseInt(e.target.value));
            });
        }
    }

    /**
     * Display search results
     * @param {Array} results - Array of objects with visibility and scoring data
     * @param {Object} searchParams - Original search parameters
     * @param {Object} statistics - Search statistics
     */
    async displayResults(results, searchParams, statistics) {
        console.log('ResultsDisplay.displayResults called with:', results?.length || 0, 'results');
        console.log('ResultsDisplay - Sample result:', results?.[0]);
        console.log('ResultsDisplay - Search params:', searchParams);

        this.currentResults = results;
        this.currentSearchParams = searchParams;

        // Show results section
        const resultsSection = document.getElementById('resultsSection');
        if (resultsSection) {
            resultsSection.style.display = 'block';
            console.log('ResultsDisplay - Results section shown');
        } else {
            console.error('ResultsDisplay - resultsSection element not found!');
        }

        // Display summary
        this.displaySummary(results, searchParams, statistics);

        // Update quality tier counts
        this.updateQualityTabs(results);

        // Display table
        await this.updateResultsTable();

        // Ensure DOM is fully rendered before continuing
        await new Promise(resolve => {
            requestAnimationFrame(() => {
                setTimeout(resolve, 100); // Small delay to ensure table is rendered
            });
        });

        console.log(`ResultsDisplay - Displaying ${results.length} search results`);
    }

    /**
     * Display search results summary
     * @param {Array} results - Search results
     * @param {Object} searchParams - Search parameters
     * @param {Object} statistics - Search statistics
     */
    displaySummary(results, searchParams, statistics) {
        const summaryDiv = document.getElementById('resultsSummary');
        if (!summaryDiv) return;

        const direction = this.getDirectionText(searchParams.azimuth);
        const dateStr = new Date(searchParams.observationDate).toLocaleDateString();

        summaryDiv.innerHTML = `
            <div class="row g-3">
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="summary-value">${results.length}</div>
                        <div class="summary-label">Objects Found</div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="summary-value">${direction}</div>
                        <div class="summary-label">Search Direction</div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="summary-value">${statistics ? statistics.durationRange.avg.toFixed(1) + 'h' : 'N/A'}</div>
                        <div class="summary-label">Avg Duration</div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="summary-value">${dateStr}</div>
                        <div class="summary-label">Observation Date</div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Update quality tier tabs with counts
     * @param {Array} results - Search results
     */
    updateQualityTabs(results) {
        const tiers = ['excellent', 'good', 'fair'];
        const thresholds = { excellent: 80, good: 65, fair: 50 };

        tiers.forEach(tier => {
            const tab = document.querySelector(`[data-tier="${tier}"]`);
            if (tab) {
                const count = results.filter(obj => {
                    const score = obj.score.total;
                    const minScore = thresholds[tier];
                    const maxScore = tier === 'excellent' ? 100 :
                                   tier === 'good' ? thresholds.excellent :
                                   thresholds.good;
                    return score >= minScore && score < maxScore;
                }).length;

                // Update tab text with count
                const icon = tab.querySelector('i').outerHTML;
                const text = tab.textContent.split('(')[0].trim();
                tab.innerHTML = `${icon} ${text} ${count > 0 ? `(${count})` : ''}`;
            }
        });
    }

    /**
     * Update the results table display
     */
    async updateResultsTable() {
        // Filter results based on current tier
        let filteredResults = this.filterByTier(this.currentResults, this.currentTier);

        // Sort results
        filteredResults = this.sortResults(filteredResults, this.currentSort);

        // Limit results
        this.displayedResults = filteredResults.slice(0, this.maxResults);

        // Update table
        await this.renderResultsTable(this.displayedResults);

        // Show/hide no results message
        this.toggleNoResultsMessage(this.displayedResults.length === 0);
    }

    /**
     * Render the results table
     * @param {Array} results - Results to display
     */
    async renderResultsTable(results) {
        const tableBody = document.getElementById('resultsTableBody');
        if (!tableBody) return;

        if (results.length === 0) {
            tableBody.innerHTML = '<tr><td colspan="8" class="text-center text-muted">No objects match the current filters</td></tr>';
            return;
        }

        // Get preferred display names for all objects
        const rowsPromises = results.map(async (obj, index) => {
            const rank = index + 1;

            // Catalog designation as the display name (see displayNames.js)
            const { displayName } = await this.resolveNames(obj);
            const storedName = cleanObjectName(obj.name || obj.designation || '');

            const type = objectTypeLabel(obj) || 'Unknown';
            const magnitude = obj.magnitude ? obj.magnitude.toFixed(1) : 'N/A';
            const duration = obj.visibility.moonFreeDuration.toFixed(1);
            const score = obj.score.total.toFixed(1);

            // Score color coding
            const scoreClass = score >= 80 ? 'text-success' :
                              score >= 65 ? 'text-primary' :
                              score >= 50 ? 'text-warning' : 'text-secondary';

            // Check if object is bookmarked
            const isBookmarked = window.bookmarkManager ? window.bookmarkManager.isBookmarked(obj.name || obj.designation) : false;

            return `
                <tr data-object-id="${index}">
                    <td class="text-center">
                        <button class="btn btn-sm btn-link p-0" onclick="window.resultsDisplay.toggleBookmark('${obj.name || obj.designation}', ${index})" title="${isBookmarked ? 'Remove bookmark' : 'Add bookmark'}">
                            <i class="fas fa-bookmark ${isBookmarked ? 'text-warning' : 'text-muted'}" id="bookmark-icon-${index}"></i>
                        </button>
                    </td>
                    <td>
                        <span class="badge bg-primary">#${rank}</span>
                    </td>
                    <td>
                        <div class="object-name">
                            <strong>${displayName}</strong>
                            ${storedName && displayName !== storedName ?
                                `<small class="text-muted d-block">Also: ${storedName}</small>` : ''
                            }
                            ${obj.ra_hours && obj.dec_degrees ?
                                `<small class="text-muted d-block">RA: ${obj.ra_hours.toFixed(2)}h, Dec: ${obj.dec_degrees.toFixed(1)}°</small>` : ''
                            }
                        </div>
                    </td>
                    <td>
                        <span class="badge bg-secondary">${type}</span>
                    </td>
                    <td>${magnitude}</td>
                    <td>
                        <div>
                            <strong>${duration}h</strong>
                            <small class="text-muted d-block">${obj.visibility.darkTimeDuration.toFixed(1)}h dark</small>
                        </div>
                    </td>
                    <td>
                        <span class="badge ${scoreClass.replace('text-', 'bg-')}">${score}</span>
                    </td>
                    <td>
                        <button class="btn btn-sm btn-outline-primary me-1" onclick="window.resultsDisplay.openInCalculator(${index})" title="Open in Visibility Calculator">
                            <i class="fas fa-calculator fa-fw"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-primary" onclick="window.resultsDisplay.showObjectDetails(${index})" title="Show details">
                            <i class="fas fa-info-circle fa-fw"></i>
                        </button>
                    </td>
                </tr>
            `;
        });

        const rows = await Promise.all(rowsPromises);
        tableBody.innerHTML = rows.join('');
    }

    /**
     * Filter results by quality tier
     * @param {Array} results - Results to filter
     * @param {string} tier - Quality tier
     * @returns {Array} Filtered results
     */
    filterByTier(results, tier) {
        if (tier === 'all') return results;

        const thresholds = { excellent: 80, good: 65, fair: 50 };
        const minScore = thresholds[tier];
        const maxScore = tier === 'excellent' ? 100 :
                        tier === 'good' ? thresholds.excellent :
                        thresholds.good;

        return results.filter(obj => {
            const score = obj.score.total;
            return score >= minScore && score < maxScore;
        });
    }

    /**
     * Sort results by specified criteria
     * @param {Array} results - Results to sort
     * @param {string} criteria - Sort criteria
     * @returns {Array} Sorted results
     */
    sortResults(results, criteria) {
        return [...results].sort((a, b) => {
            switch (criteria) {
                case 'score':
                    return b.score.total - a.score.total;
                case 'moonFreeDuration':
                    return b.visibility.moonFreeDuration - a.visibility.moonFreeDuration;
                case 'averageAltitude':
                    return b.visibility.averageAltitude - a.visibility.averageAltitude;
                case 'magnitude':
                    const magA = a.magnitude || 99;
                    const magB = b.magnitude || 99;
                    return magA - magB; // Lower magnitude = brighter = better
                case 'objectType':
                    return b.score.breakdown.objectType - a.score.breakdown.objectType;
                default:
                    return b.score.total - a.score.total;
            }
        });
    }

    /**
     * Set quality tier filter
     * @param {string} tier - Quality tier
     */
    async setQualityTier(tier) {
        this.currentTier = tier;

        // Update active tab
        document.querySelectorAll('#qualityTabs .nav-link').forEach(tab => {
            tab.classList.remove('active');
            if (tab.getAttribute('data-tier') === tier) {
                tab.classList.add('active');
            }
        });

        await this.updateResultsTable();
    }

    /**
     * Set sort criteria
     * @param {string} criteria - Sort criteria
     */
    async setSortCriteria(criteria) {
        this.currentSort = criteria;
        await this.updateResultsTable();
    }

    /**
     * Set maximum results to display
     * @param {number} max - Maximum results
     */
    async setMaxResults(max) {
        this.maxResults = max;
        await this.updateResultsTable();
    }

    /**
     * Show detailed information for an object
     * @param {number} index - Object index in displayed results
     */
    async showObjectDetails(index) {
        const obj = this.displayedResults[index];
        if (!obj) return;

        // Get object data with display name and aliases
        const { displayName, allAliases } = await this.getObjectNamesAndAliases(obj);

        // Populate modal with object details
        this.populateObjectModal(obj, displayName, allAliases);

        // Show the modal
        const modal = new bootstrap.Modal(document.getElementById('objectDetailsModal'));
        modal.show();
    }

    /**
     * Open object in the main visibility calculator
     * @param {number} index - Object index in displayed results
     */
    openInCalculator(index) {
        const obj = this.displayedResults[index];
        if (!obj) return;

        const params = new URLSearchParams();
        params.set('ra', obj.ra_hours);
        params.set('dec', obj.dec_degrees);
        params.set('name', obj.name || obj.designation || 'Custom Object');

        // Include location from search params
        if (this.currentSearchParams) {
            if (this.currentSearchParams.latitude) params.set('lat', this.currentSearchParams.latitude);
            if (this.currentSearchParams.longitude) params.set('lon', this.currentSearchParams.longitude);
            if (this.currentSearchParams.observationDate) {
                // Handle both Date objects and date strings
                const date = this.currentSearchParams.observationDate;
                params.set('date', date instanceof Date ? date.toISOString().split('T')[0] : date);
            }
        }

        // Use anchor element approach for better browser compatibility
        const link = document.createElement('a');
        link.href = `visibility-calculator.html?${params.toString()}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
    }

    /**
     * Toggle bookmark for an object
     * @param {string} objectName - Object name or designation
     * @param {number} index - Object index in displayed results
     */
    toggleBookmark(objectName, index) {
        if (!window.bookmarkManager) {
            console.error('BookmarkManager not available');
            return;
        }

        const obj = this.displayedResults[index];
        if (!obj) return;

        const isCurrentlyBookmarked = window.bookmarkManager.isBookmarked(objectName);

        if (isCurrentlyBookmarked) {
            // Remove bookmark
            window.bookmarkManager.removeBookmark(objectName);
        } else {
            // Add bookmark with metadata
            window.bookmarkManager.bookmarkObject(objectName, {
                type: objectTypeLabel(obj),
                magnitude: obj.magnitude || null,
                ra: obj.ra_hours || null,
                dec: obj.dec_degrees || null,
                notes: ''
            });
        }

        // Update the icon
        const icon = document.getElementById(`bookmark-icon-${index}`);
        if (icon) {
            if (isCurrentlyBookmarked) {
                icon.classList.remove('text-warning');
                icon.classList.add('text-muted');
            } else {
                icon.classList.remove('text-muted');
                icon.classList.add('text-warning');
            }
        }

        // Show feedback message
        const message = isCurrentlyBookmarked ?
            `Removed ${objectName} from bookmarks` :
            `Added ${objectName} to bookmarks`;
        this.showToast(message, isCurrentlyBookmarked ? 'info' : 'success');
    }

    /**
     * Show a toast notification
     * @param {string} message - Message to display
     * @param {string} type - Toast type (success, info, warning, danger)
     */
    showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `alert alert-${type} alert-dismissible fade show`;
        toast.style.cssText = 'position: fixed; bottom: 20px; right: 20px; z-index: 1060; min-width: 300px;';
        toast.innerHTML = `
            <i class="fas fa-${type === 'success' ? 'check-circle' : 'info-circle'} me-2"></i>${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;
        document.body.appendChild(toast);

        setTimeout(() => {
            toast.remove();
        }, 3000);
    }

    /**
     * Get object display name and all aliases
     * @param {Object} obj - Object data
     * @returns {Promise<Object>} Object with displayName and allAliases array
     */
    async getObjectNamesAndAliases(obj) {
        const { displayName, aliases } = await this.resolveNames(obj);
        const allAliases = [...new Set([obj.name || obj.designation, ...aliases].filter(Boolean))].sort();
        return { displayName, allAliases };
    }

    /**
     * Populate the object details modal
     * @param {Object} obj - Object data
     * @param {string} displayName - Preferred display name
     * @param {Array} allAliases - All known aliases
     */
    populateObjectModal(obj, displayName, allAliases) {
        const vis = obj.visibility;
        const score = obj.score;

        // Set modal title
        document.getElementById('objectTitle').textContent = displayName;

        // Populate aliases
        const aliasesContainer = document.getElementById('objectAliases');
        if (allAliases.length > 1) {
            const aliasesHtml = allAliases.map(alias => {
                const isPrimary = alias === displayName;
                const badgeClass = isPrimary ? 'bg-primary' : 'bg-secondary';
                return `<span class="badge ${badgeClass}">${alias}</span>`;
            }).join(' ');
            aliasesContainer.innerHTML = aliasesHtml;
        } else {
            aliasesContainer.innerHTML = `<span class="badge bg-primary">${displayName}</span>`;
        }

        // Populate coordinates
        const coordinatesContainer = document.getElementById('objectCoordinates');
        let coordinatesHtml = '';
        if (obj.ra_hours && obj.dec_degrees) {
            coordinatesHtml += `
                <div class="coordinates-item">
                    <span class="coordinates-label">RA:</span>
                    <span class="coordinates-value">${obj.ra_hours.toFixed(3)}h</span>
                </div>
                <div class="coordinates-item">
                    <span class="coordinates-label">Dec:</span>
                    <span class="coordinates-value">${obj.dec_degrees.toFixed(2)}°</span>
                </div>
            `;
        }
        if (obj.magnitude) {
            coordinatesHtml += `
                <div class="coordinates-item">
                    <span class="coordinates-label">Magnitude:</span>
                    <span class="coordinates-value">${obj.magnitude}</span>
                </div>
            `;
        }
        coordinatesContainer.innerHTML = coordinatesHtml;

        // Populate overall score
        document.getElementById('overallScore').textContent = Math.round(score.total);

        // Populate score breakdown
        const scoreBreakdownContainer = document.getElementById('scoreBreakdown');
        const scoreItems = [
            { label: 'Moon-free time', value: score.breakdown.moonFreeDuration },
            { label: 'Dark time', value: score.breakdown.darkTimeDuration },
            { label: 'Average altitude', value: score.breakdown.averageAltitude },
            { label: 'Peak altitude', value: score.breakdown.peakAltitude },
            { label: 'Object type', value: score.breakdown.objectType },
            { label: 'Brightness', value: score.breakdown.magnitude }
        ];

        const scoreBreakdownHtml = scoreItems.map(item => {
            const scoreClass = item.value >= 80 ? 'score-excellent' :
                             item.value >= 65 ? 'score-good' :
                             item.value >= 50 ? 'score-fair' : 'score-poor';
            return `
                <div class="score-breakdown-item">
                    <span class="score-breakdown-label">${item.label}</span>
                    <span class="score-breakdown-value ${scoreClass}">${Math.round(item.value)}</span>
                </div>
            `;
        }).join('');
        scoreBreakdownContainer.innerHTML = scoreBreakdownHtml;

        // Populate visibility times
        const visibilityTimesContainer = document.getElementById('visibilityTimes');
        let visibilityTimesHtml = '';
        if (vis.riseTime && vis.setTime) {
            visibilityTimesHtml = `
                <div class="visibility-item">
                    <span class="visibility-label">Rises:</span>
                    <span class="visibility-value">${vis.riseTime.toLocaleTimeString()}</span>
                </div>
                <div class="visibility-item">
                    <span class="visibility-label">Sets:</span>
                    <span class="visibility-value">${vis.setTime.toLocaleTimeString()}</span>
                </div>
            `;
        } else {
            visibilityTimesHtml = '<div class="text-muted">Visibility times not calculated</div>';
        }
        visibilityTimesContainer.innerHTML = visibilityTimesHtml;

        // Populate visibility statistics
        const visibilityStatsContainer = document.getElementById('visibilityStats');
        const visibilityStatsHtml = `
            <div class="visibility-item">
                <span class="visibility-label">Total duration:</span>
                <span class="visibility-value">${vis.totalDuration.toFixed(1)}h</span>
            </div>
            <div class="visibility-item">
                <span class="visibility-label">Dark time:</span>
                <span class="visibility-value">${vis.darkTimeDuration.toFixed(1)}h</span>
            </div>
            <div class="visibility-item">
                <span class="visibility-label">Moon-free:</span>
                <span class="visibility-value">${vis.moonFreeDuration.toFixed(1)}h</span>
            </div>
            <div class="visibility-item">
                <span class="visibility-label">Peak altitude:</span>
                <span class="visibility-value">${vis.peakAltitude.toFixed(0)}°</span>
            </div>
        `;
        visibilityStatsContainer.innerHTML = visibilityStatsHtml;

        // Populate object properties
        const objectPropertiesContainer = document.getElementById('objectProperties');
        let propertiesHtml = '';
        if (objectTypeLabel(obj)) {
            propertiesHtml += `
                <div class="coordinates-item">
                    <span class="coordinates-label">Type:</span>
                    <span class="coordinates-value">${objectTypeLabel(obj)}</span>
                </div>
            `;
        }
        if (obj.catalog) {
            propertiesHtml += `
                <div class="coordinates-item">
                    <span class="coordinates-label">Catalog:</span>
                    <span class="coordinates-value">${obj.catalog}</span>
                </div>
            `;
        }
        objectPropertiesContainer.innerHTML = propertiesHtml || '<div class="text-muted">No additional properties available</div>';
    }

    /**
     * Get detailed explanation for an object
     * @param {Object} obj - Object with visibility and score data
     * @returns {Promise<string>} Detailed explanation
     */
    async getObjectExplanation(obj) {
        const vis = obj.visibility;
        const score = obj.score;

        // Get preferred display name and all aliases
        const { displayName, allAliases } = await this.getObjectNamesAndAliases(obj);

        let explanation = `${displayName}\n\n`;

        // Add aliases section if we have multiple names
        if (allAliases.length > 1) {
            explanation += `Also known as:\n`;
            const otherAliases = allAliases.filter(alias => alias !== displayName);
            explanation += otherAliases.map(alias => `• ${alias}`).join('\n');
            explanation += '\n\n';
        }

        explanation += `Overall Score: ${score.total}/100\n\n`;
        explanation += `Visibility Details:\n`;
        explanation += `• Moon-free time: ${vis.moonFreeDuration.toFixed(1)} hours (${score.breakdown.moonFreeDuration}/100)\n`;
        explanation += `• Dark time: ${vis.darkTimeDuration.toFixed(1)} hours (${score.breakdown.darkTimeDuration}/100)\n`;
        explanation += `• Average altitude: ${vis.averageAltitude.toFixed(0)}° (${score.breakdown.averageAltitude}/100)\n`;
        explanation += `• Peak altitude: ${vis.peakAltitude.toFixed(0)}° (${score.breakdown.peakAltitude}/100)\n`;
        explanation += `• Object type suitability: ${score.breakdown.objectType}/100\n`;
        explanation += `• Brightness factor: ${score.breakdown.magnitude}/100\n\n`;

        if (vis.riseTime && vis.setTime) {
            explanation += `Visibility Window:\n`;
            explanation += `• Rises: ${vis.riseTime.toLocaleTimeString()}\n`;
            explanation += `• Sets: ${vis.setTime.toLocaleTimeString()}\n`;
        }

        return explanation;
    }

    /**
     * Toggle no results message
     * @param {boolean} show - Whether to show the message
     */
    toggleNoResultsMessage(show) {
        const noResultsMsg = document.getElementById('noResultsMessage');
        const resultsTable = document.querySelector('.results-table');

        if (noResultsMsg) {
            noResultsMsg.style.display = show ? 'block' : 'none';
        }
        if (resultsTable) {
            resultsTable.style.display = show ? 'none' : 'block';
        }
    }

    /**
     * Get direction text from azimuth
     * @param {number} azimuth - Azimuth in degrees
     * @returns {string} Direction text
     */
    getDirectionText(azimuth) {
        const directions = [
            'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
            'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
        ];
        const index = Math.round(azimuth / 22.5) % 16;
        return directions[index] + ` (${azimuth}°)`;
    }

    /**
     * Clear results display
     */
    clearResults() {
        this.currentResults = [];
        this.displayedResults = [];

        const resultsSection = document.getElementById('resultsSection');
        if (resultsSection) {
            resultsSection.style.display = 'none';
        }
    }

    /**
     * Get current display statistics
     * @returns {Object} Display statistics
     */
    getDisplayStatistics() {
        return {
            totalResults: this.currentResults.length,
            displayedResults: this.displayedResults.length,
            currentTier: this.currentTier,
            currentSort: this.currentSort,
            maxResults: this.maxResults
        };
    }
}

export default ResultsDisplay;